import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../stores/root-store-context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/layout/Layout";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  FormProps,
  Input,
  message,
  Select,
  SelectProps,
  Spin,
  Tag,
  Upload,
  Switch,
  Divider,
  ConfigProvider,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";

import {
  DeleteOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  GoogleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import FontSettings, { CompanyFonts } from "../components/elements/KanbanBoard/Font/FontSettings";
import { CompanyWebhook } from "../models/CompanyWebhook";
import { Company } from "../models/Company";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const jsonToArray = (json?: string): { key: string; value: string }[] => {
  if (!json) return [];
  try {
    const obj = JSON.parse(json);
    return Object.entries(obj).map(([k, v]) => ({
      key: k,
      value: String(v),
    }));
  } catch {
    return [];
  }
};

const arrayToJson = (arr: { key: string; value: string }[]) =>
  JSON.stringify(
    arr.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key) acc[key] = value;
      return acc;
    }, {}),
  );

const parseFontsJson = (json?: string): CompanyFonts => {
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const stringifyFonts = (fonts?: CompanyFonts): string => {
  if (!fonts || Object.keys(fonts).length === 0) return "";
  return JSON.stringify(fonts);
};

const CompanySettings: React.FC = observer(() => {
  const { applicationStore, countryStore } = useStore();
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [enableGoogleSync, setEnableGoogleSync] = useState(false);
  const [enableTeamsSync, setEnableTeamsSync] = useState(false);
  const navigate = useNavigate();
  const uploadRef = useRef<any>(null);

  const fileChange = (info: UploadChangeParam) => {
    setFileList([...info.fileList].slice(-1));
  };

  useEffect(() => {
    countryStore.GetAllAvailableCountries();
  }, [countryStore]);

  const countryOptions = React.useMemo(
    () => countryStore.toOptions(i18n?.language || "en", { withFlags: true }),
    [i18n?.language, countryStore.availableCountries.length]
  );

  useEffect(() => {
    if (!applicationStore.company) return;

    const companyForForm: Company = {
      ...applicationStore.company,
      CountryId: applicationStore.company.CountryId ?? undefined,
      Webhooks: (applicationStore.company.Webhooks ?? []).map((w) => ({
        ...w,
        headers: jsonToArray(w.headersJson),
      })) as any,
      Fonts: parseFontsJson(applicationStore.company.FontsJson),
    };

    console.log("Company for form:", companyForForm);
    form.setFieldsValue(companyForForm);

    setEnableGoogleSync(applicationStore.company.EnableGoogleSync || false);
    setEnableTeamsSync(applicationStore.company.EnableTeamsSync || false);

    if (applicationStore.company.LogoId) {
      setFileList([
        {
          uid: "-1",
          name: applicationStore.company.LogoId,
          status: "done",
          url: `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/${applicationStore.company.LogoId}`,
        },
      ]);
    }
  }, [applicationStore.company, form]);

  const onFinish: FormProps<any>["onFinish"] = async (values: Company) => {
    messageApi.open({ type: "loading", content: "Saving…", key: "saving" });

    if (fileList.length && fileList[0].uid !== "-1") {
      await applicationStore.uploadPhoto(
        fileList[0],
        applicationStore.company?.Id || "",
      );
    }

    const payload: Company = {
      ...values,
      Webhooks: (values.Webhooks ?? []).map((w: any) => {
        const { headers = [], ...rest } = w;
        return {
          ...rest,
          headersJson: arrayToJson(headers),
        } as CompanyWebhook;
      }),
      FontsJson: stringifyFonts(values.Fonts),
    };

    console.log("Payload to save:", payload);

    applicationStore
      .editCompany(payload)
      .then(() => {
        messageApi.success("Successfully saved!", 1.5);
        applicationStore.updateCompany();
      })
      .finally(() => messageApi.destroy("saving"));
  };

  const optionsDays: SelectProps["options"] = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const tagRender: SelectProps["tagRender"] = (props) => {
    const { label, closable, onClose } = props;
    return (
      <Tag color="error" closable={closable} onClose={onClose}>
        {label}
      </Tag>
    );
  };

  const eventOptions = [
    { value: "PreUserAdd", label: t("Pre-add user web-hook URL") },
    { value: "PostUserAdd", label: t("Post-add user web-hook URL") },
  ];

  const timezoneOptions = [
    { value: "UTC", label: "UTC" },
    { value: "Eastern Standard Time", label: "Eastern Time (US & Canada)" },
    { value: "Central Standard Time", label: "Central Time (US & Canada)" },
    { value: "Mountain Standard Time", label: "Mountain Time (US & Canada)" },
    { value: "Pacific Standard Time", label: "Pacific Time (US & Canada)" },
    { value: "GMT Standard Time", label: "London (GMT)" },
    { value: "Central European Standard Time", label: "Central European Time" },
    { value: "E. Europe Standard Time", label: "Eastern European Time" },
  ];

  const testWebhook = async (
    formRef: FormInstance,
    idx: number,
    httpMethod: "Get" | "Post",
  ) => {
    const current = formRef.getFieldValue(["Webhooks", idx]);
    const method = httpMethod === "Get" ? "GET" : "HEAD";

    try {
      await fetch(current.url, { method, mode: "no-cors" });
      message.success("Request reached the server (opaque response)");
    } catch (e: any) {
      message.error(e.message ?? "Network error");
    }
  };

  return (
    <Layout headerStyle={1} footerStyle={2}>
      {contextHolder}
      <div style={{ maxWidth: 750, margin: "20px auto 100px" }}>
        <Button onClick={() => navigate(-1)}>{t("Back")}</Button>

        <Spin spinning={applicationStore.isLoading}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item>
              <Flex vertical align="center">
                <Upload
                  ref={uploadRef}
                  accept="image/*"
                  maxCount={1}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={fileChange}
                  beforeUpload={(file) => {
                    const isLt10Mb = file.size <= MAX_FILE_SIZE;

                    if (!isLt10Mb) {
                      message.error(t("fileSizeError"));
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                  }}
                >
                  {fileList.length === 0 && <UploadOutlined />}
                </Upload>
                {fileList.length > 0 && (
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() =>
                      (document.querySelector(
                        'input[type="file"]',
                      ) as HTMLElement).click()
                    }
                  >
                    {t("Upload")}
                  </Button>
                )}
              </Flex>
            </Form.Item>

            <Form.Item name="Id">
              <Input hidden />
            </Form.Item>

            <Form.Item
              label={t("CompanyName")}
              name="Title"
              rules={[{ required: true }]}
            >
              <Input placeholder={t("CompanyName")} />
            </Form.Item>

            <Form.Item
              label={t("Tenant")}
              name="Tenant"
              rules={[{ required: true }]}
            >
              <Input disabled placeholder={t("Tenant")} />
            </Form.Item>

            <Form.Item
              label={t("CompanyDescription")}
              name="Description"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={t("Country")}
              name="CountryId"
              rules={[{ required: true, message: t("ChooseCountry") }]}>
              <Select
                showSearch
                placeholder={t("Country")}
                options={countryOptions}
                optionFilterProp="name"
                optionLabelProp="name"
                filterOption={(input, option) =>
                  (option as any)?.name?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              label={t("Address")}
              name="Adress"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              hidden={!applicationStore.siteConfig.isShowLanguage}
              label={t("CompanyNotificationLanguage")}
              name="NotificationLanguage"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "en", label: t("EnLang") },
                  { value: "ru", label: t("RuLang") },
                  { value: "uk", label: t("UkLang") },
                ]}
              />
            </Form.Item>

            <Form.Item
              label={t("Currency")}
              name="Currency"
              rules={[{ required: true }]}
            >
              <Select
                disabled={!applicationStore.siteConfig.isShowLanguage}
                options={[
                  { value: "$", label: "$ USD" },
                  { value: "€", label: "€ EUR" },
                  { value: "₴", label: "₴ UAH" },
                  { value: "£", label: "£ GBP" },
                ]}
              />
            </Form.Item>

            <Form.Item label={t("WeekendDays")} name="WeekendDays">
              <Select mode="multiple" tagRender={tagRender} options={optionsDays} />
            </Form.Item>

            <Form.Item
              label={t("vacationWorkHours")}
              name="HoursVacation"
              rules={[{ required: true }]}
            >
              <Input type="number" max={24} />
            </Form.Item>

            <Form.Item
              label={t("sickLeaveWorkHours")}
              name="HoursSickLeave"
              rules={[{ required: true }]}
            >
              <Input type="number" max={24} />
            </Form.Item>

            <Divider>Calendar Synchronization</Divider>

            <Card
              size="small"
              title={
                <Flex align="center" gap={8}>
                  <GoogleOutlined style={{ fontSize: 20, color: "#4285F4" }} />
                  <span>Google Calendar Integration</span>
                </Flex>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item
                label="Enable Google Calendar Sync"
                name="EnableGoogleSync"
                valuePropName="checked"
              >
                <Switch onChange={setEnableGoogleSync} />
              </Form.Item>

              {enableGoogleSync && (
                <>
                  <Form.Item
                    label="Google Email Domain"
                    name="GoogleEmailDomain"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <Input placeholder="example.com" />
                  </Form.Item>

                  <Form.Item
                    label="Google Application Name"
                    name="GoogleApplicationName"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <Input placeholder="My Company Calendar App" />
                  </Form.Item>

                  <Form.Item
                    label="Google Calendar ID"
                    name="GoogleCalendarId"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <Input placeholder="primary or calendar@group.calendar.google.com" />
                  </Form.Item>

                  <Form.Item
                    label="Google Service Account Credentials (JSON)"
                    name="GoogleCredentialsJson"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <TextArea
                      rows={6}
                      placeholder='{"type": "service_account", "project_id": "...", ...}'
                    />
                  </Form.Item>
                </>
              )}
            </Card>

            <Card
              size="small"
              title={
                <Flex align="center" gap={8}>
                  <TeamOutlined style={{ fontSize: 20, color: "#5B5FC7" }} />
                  <span>Microsoft Teams/Graph Integration</span>
                </Flex>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item
                label="Enable Microsoft Teams Sync"
                name="EnableTeamsSync"
                valuePropName="checked"
              >
                <Switch onChange={setEnableTeamsSync} />
              </Form.Item>

              {enableTeamsSync && (
                <>
                  <Form.Item
                    label="Teams Email Domain"
                    name="TeamsEmailDomain"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="example.com" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Tenant ID"
                    name="GraphTenantId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Client ID"
                    name="GraphClientId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Client Secret"
                    name="GraphClientSecret"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input.Password placeholder="Client secret value" />
                  </Form.Item>

                  <Form.Item
                    label="Graph Calendar ID"
                    name="GraphCalendarId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="Calendar ID or email address" />
                  </Form.Item>

                  <Form.Item
                    label="Graph Time Zone"
                    name="GraphTimeZone"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Select options={timezoneOptions} placeholder="Select timezone" />
                  </Form.Item>
                </>
              )}
            </Card>

            <Divider>Font Settings</Divider>
            <Form.Item name="Fonts">
              <FontSettings />
            </Form.Item>

            <Divider>Webhooks</Divider>
            <Form.List name="Webhooks">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Card key={key} size="small" style={{ marginBottom: 14 }}>
                      <Flex gap={8} align="flex-start" wrap="wrap">
                        <Form.Item
                          {...rest}
                          name={[name, "eventType"]}
                          label={t("Event")}
                          rules={[{ required: true }]}
                          style={{ width: 180 }}
                        >
                          <Select options={eventOptions} />
                        </Form.Item>

                        <Form.Item
                          {...rest}
                          name={[name, "httpMethod"]}
                          label={t("Method")}
                          rules={[{ required: true }]}
                          style={{ width: 120 }}
                        >
                          <Select
                            options={[
                              { value: "Post", label: "POST" },
                              { value: "Get", label: "GET" },
                            ]}
                          />
                        </Form.Item>

                        <Form.Item
                          {...rest}
                          name={[name, "url"]}
                          label="URL"
                          style={{ flex: 1, minWidth: 250 }}
                          rules={[
                            { required: true, message: t("Enter URL") },
                            { type: "url", message: t("URL is invalid") },
                          ]}
                        >
                          <Input placeholder="https://example.com/hook" />
                        </Form.Item>
                      </Flex>

                      <Form.List name={[name, "headers"]}>
                        {(hdrFields, hdrOps) => (
                          <>
                            {hdrFields.map(({ key: hk, name: hn, ...hr }) => (
                              <Flex
                                key={hk}
                                gap={8}
                                align="baseline"
                                style={{ marginBottom: 6 }}
                              >
                                <Form.Item
                                  {...hr}
                                  name={[hn, "key"]}
                                  style={{ flex: 1 }}
                                  rules={[{ required: true }]}
                                >
                                  <Input placeholder="Header name" />
                                </Form.Item>
                                <Form.Item
                                  {...hr}
                                  name={[hn, "value"]}
                                  style={{ flex: 2 }}
                                  rules={[{ required: true }]}
                                >
                                  <Input placeholder="Header value" />
                                </Form.Item>
                                <MinusCircleOutlined
                                  onClick={() => hdrOps.remove(hn)}
                                />
                              </Flex>
                            ))}

                            <Form.Item>
                              <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                block
                                onClick={() => hdrOps.add({ key: "", value: "" })}
                              >
                                {t("Add header")}
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>

                      <Flex justify="space-between">
                        <Button
                          icon={<LinkOutlined />}
                          onClick={() =>
                            testWebhook(
                              form,
                              name,
                              form.getFieldValue(["Webhooks", name, "httpMethod"]),
                            )
                          }
                        >
                          {t("Test")}
                        </Button>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Flex>
                    </Card>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          eventType: "PreUserAdd",
                          httpMethod: "Post",
                          url: "",
                          headers: [{ key: "", value: "" }],
                        })
                      }
                    >
                      {t("Add web-hook")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                style={{ height: 48, float: "right" }}
              >
                {t("Save")}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Layout>
  );
});

export default CompanySettings;